import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { TokenSyncMessage, TokenSyncMessageType, UserTokens } from '../entities/token-sync.entity';
import { Login } from '../state/authentication.actions';

/**
 * Service for managing token synchronization across multiple tabs using SharedWorker
 *
 * This service provides:
 * - Token storage and retrieval via a shared worker
 * - Automatic token updates across multiple tabs
 * - Observable streams for token changes
 * - Support for token refresh operations
 *
 * Notes:
 * - Even better would be to extend this to a SessionSyncService and also hold the user information, but for now it only syncs tokens. User info can be fetched separately in each tab using the synced tokens.
 */
@Injectable({
    providedIn: 'root'
})
export class TokenSyncService implements OnDestroy {
    private sharedWorker: SharedWorker | null = null;
    private destroy$ = new Subject<void>();
    private pendingRequests = new Map<string, (tokens: UserTokens | null) => void>();

    private $tokens = signal<UserTokens | null>(null);
    public tokens$ = toObservable(this.$tokens);

    private readonly router = inject(Router);
    private readonly store = inject(Store);

    private readonly SESSION_STORAGE_KEY = 'token-sync-temp-tokens';

    /**
     * Current tokens value (snapshot)
     */
    public get tokens(): UserTokens | null {
        return this.$tokens();
    }

    constructor() {
        this.setupBeforeUnloadListener();
        this.restoreTokensFromSessionStorage();
        this.initializeSharedWorker();
    }

    // #region Store and restore token on refresh
    /**
     * Set up listener to save tokens to sessionStorage before page unload
     */
    private setupBeforeUnloadListener(): void {
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this.saveTokensToSessionStorage();
            });
        }
    }

    /**
     * Save current tokens to sessionStorage for recovery after page refresh
     */
    private saveTokensToSessionStorage(): void {
        const tokens = this.$tokens();
        if (tokens) {
            try {
                sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(tokens));
            } catch (error) {
                console.warn('[TokenSyncService] Failed to save tokens to sessionStorage:', error);
            }
        }
    }

    /**
     * Restore tokens from sessionStorage if they exist (after page refresh)
     */
    private restoreTokensFromSessionStorage(): void {
        try {
            const storedTokens = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
            if (storedTokens) {
                const tokens: UserTokens = JSON.parse(storedTokens);
                this.updateTokens(tokens);
                this.removeTokensFromSessionStorage();
            }
        } catch (error) {
            console.warn('[TokenSyncService] Failed to restore tokens from sessionStorage:', error);
        }
    }

    private removeTokensFromSessionStorage(): void {
        sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
    }
    // # endregion

    /**
     * Initialize connection to the shared worker
     */
    private initializeSharedWorker(): void {
        try {
            // Check if SharedWorker is supported
            if (typeof SharedWorker === 'undefined') {
                console.warn('[TokenSyncService] SharedWorker is not supported in this browser');
                return;
            }

            // Get the base href from the <base> tag (set by Angular)
            // This ensures the worker path respects the --base-href configuration
            const baseHref = document.querySelector('base')?.href || '/';

            // Construct the worker path respecting the base href
            // Remove trailing slash from baseHref to avoid double slashes
            const cleanBaseHref = baseHref.replace(/\/$/, '');
            const workerPath = `${cleanBaseHref}/assets/workers/token-sync.shared-worker.js`;

            // Create or connect to the shared worker
            this.sharedWorker = new SharedWorker(workerPath, {
                name: 'token-sync-worker'
            });

            const port = this.sharedWorker.port;

            // Set up message handler
            port.onmessage = (event: MessageEvent<TokenSyncMessage>) => {
                this.handleWorkerMessage(event.data);
            };

            // Start listening for messages
            port.start();
        } catch (error) {
            console.error('[TokenSyncService] Failed to initialize SharedWorker:', error);
        }
    }

    /**
     * Handle messages from the shared worker
     */
    private handleWorkerMessage(message: TokenSyncMessage): void {
        const { type, payload, requestId } = message;

        switch (type) {
            case TokenSyncMessageType.SET_TOKENS:
            case TokenSyncMessageType.TOKENS_UPDATED:
                this.updateTokens(payload || null);
                break;

            case TokenSyncMessageType.TOKENS_RESPONSE:
                if (!requestId) {
                    break;
                }
                const callback = this.pendingRequests.get(requestId);
                if (!callback) {
                    break;
                }
                // Update the signal so synchronous access to tokens is available after getTokens() resolves
                this.updateTokens(payload || null);
                // Resolve the pending request
                callback(payload || null);
                this.pendingRequests.delete(requestId);
                break;

            case TokenSyncMessageType.TOKENS_CLEARED:
                this.logoutSession();
                break;
        }
    }

    /**
     * Set tokens in the shared worker (typically after login/token refresh)
     */
    public setTokens(tokens: UserTokens): void {
        if (!this.sharedWorker) {
            console.warn('[TokenSyncService] SharedWorker not initialized');
            return;
        }

        this.$tokens.set(tokens);

        this.sharedWorker.port.postMessage({
            type: TokenSyncMessageType.SET_TOKENS,
            payload: tokens
        });
    }

    /**
     * Update tokens in the shared worker (typically after receiving tokens through the worker)
     */
    private updateTokens(tokens: UserTokens | null): void {
        this.$tokens.set(tokens);
        if (tokens) {
            this.store.dispatch(new Login());
        }
    }

    /**
     * Unset tokens and logout after receiving broadcast signal
     */
    private logoutSession(): void {
        if (this.$tokens() !== null) {
            this.$tokens.set(null);
            this.removeTokensFromSessionStorage();
            this.router.navigate(['/logout']);
        }
    }

    /**
     * Get current tokens from the shared worker
     * This is useful for synchronizing tokens when a tab comes into focus
     */
    public getTokens(): Promise<UserTokens | null> {
        return new Promise(resolve => {
            if (!this.sharedWorker) {
                console.warn('[TokenSyncService] SharedWorker not initialized');
                resolve(null);
                return;
            }

            // Return tokens if already restored from sessionstorage
            if (this.tokens) {
                resolve(this.tokens);
                return;
            }

            const requestId = `req_${Date.now()}_${Math.random()}`;
            this.pendingRequests.set(requestId, tokens => {
                resolve(tokens);
            });

            this.sharedWorker.port.postMessage({
                type: TokenSyncMessageType.GET_TOKENS,
                requestId
            });

            // Timeout if no response after 5 seconds
            setTimeout(() => {
                if (this.pendingRequests.has(requestId)) {
                    this.pendingRequests.delete(requestId);
                    resolve(null);
                }
            }, 5000);
        });
    }

    /**
     * Notify the shared worker that tokens have been refreshed
     * This will broadcast the new tokens to all tabs
     */
    public notifyTokenRefreshed(tokens: UserTokens): void {
        if (!this.sharedWorker) {
            console.warn('[TokenSyncService] SharedWorker not initialized');
            return;
        }
        this.$tokens.set(tokens);

        this.sharedWorker.port.postMessage({
            type: TokenSyncMessageType.TOKEN_REFRESHED,
            payload: tokens
        });
    }

    /**
     * Clear tokens in the shared worker and all tabs (typically on logout)
     */
    public clearTokens(): void {
        if (!this.sharedWorker) {
            console.warn('[TokenSyncService] SharedWorker not initialized');
            return;
        }

        // Clear sessionStorage as well just to be sure
        this.removeTokensFromSessionStorage();

        this.sharedWorker.port.postMessage({
            type: TokenSyncMessageType.CLEAR_TOKENS
        });
    }

    /**
     * Check if SharedWorker is available in the current browser
     */
    public isSupported(): boolean {
        return typeof SharedWorker !== 'undefined';
    }

    /**
     * Clean up when the service is destroyed
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.pendingRequests.clear();

        // Note: We don't close the sharedWorker port here
        // as it's shared with other tabs. Let the browser handle cleanup.
    }
}
